/**
 * ============================================================================
 * FILTER PREDICATE — applies PropertyFilter tokens to DCR records.
 * ============================================================================
 *
 * QuickSight parity: in the CTS WBR dashboard every top-bar control is a
 * row-level filter on a view column (WHERE col IN (...)). Our equivalent
 * "rows" are the per-DCR records inside each getMetrics duration; this module
 * evaluates the Cloudscape PropertyFilter query against each DcrRecord BEFORE
 * aggregation, so every table/tile/card recomputes under filters exactly like
 * QuickSight visuals do.
 *
 * Token → DcrRecord field mapping (QuickSight column in parens):
 *   orderCategory → orderCategory      (order_category)
 *   customerType  → customerType       (is_internal_customer, e.g. 'AGIDS')
 *   customer      → customer           (customer_name)
 *   locale        → locale             (locale)
 *   channel       → channelInfo[].channel     (channel)
 *   subChannel    → channelInfo[].subChannel  (sub_channel)
 *   domain        → domain             (domain)
 *   dcrStatus     → status             (dcr_status)
 *   deliveryType  → deliveryType       (delivery_type)
 *   uom           → unitOfMeasure      (unit_of_measure)
 *   dcrId         → dcrId              (dcr_id)
 *   backupDcr     → isBackupDcr        (is_backup_dcr)      Yes/No
 *   byocDcr       → isByocDcr          (is_byoc_dcr)        Yes/No
 *   otsDcr        → isOtsDcr           (is_ots_dcr)         Yes/No
 *   byotOfflineDcr→ isByotOfflineDcr   (is_byot_offline_dcr) Yes/No
 *
 * NOT row-level (handled elsewhere, like QuickSight parameters):
 *   reportPeriod (WBR/MBR cadence — the API's duration grain)
 *   metricGroup (filters metric rows, not DCRs)
 *   onTimeDeliveryThreshold (a parameter for % DCRs >= threshold calc)
 */

import { DcrRecord } from 'src/types/metricsApi.types';
import { FilterQuery } from 'src/features/dashboard/dashboardSlice';

type TokenOperator = '=' | '!=' | ':' | '!:' | '>=' | '<=' | string;

interface FilterToken {
  propertyKey?: string;
  operator: TokenOperator;
  value: string;
}

/**
 * Token keys that filter DCR rows (everything else is ignored here).
 * Exported: filterOptions.ts derives the data-driven dropdown values from
 * the same accessors, so a filterable field and its options can never drift.
 */
/** Boolean DCR flags render as the QuickSight control's Yes/No values. */
function yesNo(flag: boolean | undefined): string[] {
  return [flag ? 'Yes' : 'No'];
}

export const DCR_FIELD_ACCESSORS: Record<string, (dcr: DcrRecord) => string[]> = {
  orderCategory: (dcr) => [dcr.orderCategory ?? ''],
  // customerType is INTERNAL | AGIDS on the wire; tokenMatches compares
  // case-insensitively, so the 'AGIDS' / 'Internal' control values both work.
  customerType: (dcr) => [dcr.customerType ?? ''],
  customer: (dcr) => [dcr.customer ?? ''],
  locale: (dcr) => [dcr.locale ?? ''],
  domain: (dcr) => [dcr.domain ?? ''],
  dcrStatus: (dcr) => [dcr.status ?? ''],
  deliveryType: (dcr) => [dcr.deliveryType ?? ''],
  uom: (dcr) => [dcr.unitOfMeasure ?? ''],
  dcrId: (dcr) => [dcr.dcrId],
  channel: (dcr) => (dcr.channelInfo ?? []).map((c) => c.channel),
  subChannel: (dcr) => (dcr.channelInfo ?? []).map((c) => c.subChannel),
  backupDcr: (dcr) => yesNo(dcr.isBackupDcr),
  byocDcr: (dcr) => yesNo(dcr.isByocDcr),
  otsDcr: (dcr) => yesNo(dcr.isOtsDcr),
  backfillDcr: (dcr) => yesNo(dcr.isBackfillDcr),
  byotOfflineDcr: (dcr) => yesNo(dcr.isByotOfflineDcr),
};

function tokenMatches(token: FilterToken, dcr: DcrRecord): boolean {
  const accessor = token.propertyKey ? DCR_FIELD_ACCESSORS[token.propertyKey] : undefined;
  // Tokens for non-DCR properties (metricGroup, thresholds…) don't constrain rows.
  if (!accessor) return true;

  const values = accessor(dcr).map((v) => v.toLowerCase());
  const target = token.value.toLowerCase();

  switch (token.operator) {
    case '=':
      return values.some((v) => v === target);
    case '!=':
      return !values.some((v) => v === target);
    case ':':
      return values.some((v) => v.includes(target));
    case '!:':
      return !values.some((v) => v.includes(target));
    default:
      return true;
  }
}

/**
 * The Metric Group selection. Unlike every other control this filters METRIC
 * ROWS, not DCR rows, so it can't go through buildDcrPredicate — selectors
 * apply it to the metric catalog instead.
 *
 * Multi-select semantics (Cloudscape + QuickSight): tokens on the SAME
 * property OR together. Two `=` tokens mean "group is A or B", never
 * "A and B" (which would match nothing).
 */
export interface MetricGroupSelection {
  /** Empty = every group passes. */
  include: Set<string>;
  exclude: Set<string>;
}

export function selectedMetricGroups(query: FilterQuery | undefined): MetricGroupSelection {
  const include = new Set<string>();
  const exclude = new Set<string>();
  for (const token of (query?.tokens ?? []) as FilterToken[]) {
    if (token.propertyKey !== 'metricGroup') continue;
    if (token.operator === '=') include.add(token.value.toLowerCase());
    if (token.operator === '!=') exclude.add(token.value.toLowerCase());
  }
  return { include, exclude };
}

/** True when a metric's catalog group passes the Metric Group selection. */
export function metricGroupMatches(
  metricGroup: string | undefined,
  selection: MetricGroupSelection,
): boolean {
  const group = (metricGroup ?? '').toLowerCase();
  if (selection.exclude.has(group)) return false;
  return selection.include.size === 0 || selection.include.has(group);
}

/**
 * Build a DCR predicate from the PropertyFilter query. Tokens on the same
 * property OR together (multi-select); different properties AND together.
 * An explicit 'or' operation ORs everything. Unknown property keys never
 * constrain.
 */
export function buildDcrPredicate(query: FilterQuery | undefined): (dcr: DcrRecord) => boolean {
  const tokens = (query?.tokens ?? []) as FilterToken[];
  const dcrTokens = tokens.filter((t) => t.propertyKey && DCR_FIELD_ACCESSORS[t.propertyKey]);
  if (dcrTokens.length === 0) return () => true;

  if (query?.operation === 'or') {
    return (dcr) => dcrTokens.some((t) => tokenMatches(t, dcr));
  }

  // AND across properties, OR within a property. Two "Customer = X" /
  // "Customer = Y" chips mean either customer, not both at once — matching
  // Cloudscape multi-select and QuickSight's controls. Negations still AND
  // (excluding X and Y excludes both).
  //
  // Plain object + Object.keys rather than a Map: the build targets es5, where
  // iterating a Map needs downlevelIteration.
  const positivesByProperty: Record<string, FilterToken[]> = {};
  const negatives: FilterToken[] = [];
  for (const token of dcrTokens) {
    if (token.operator === '!=' || token.operator === '!:') {
      negatives.push(token);
      continue;
    }
    const key = token.propertyKey as string;
    if (!positivesByProperty[key]) positivesByProperty[key] = [];
    positivesByProperty[key].push(token);
  }
  const positiveGroups: FilterToken[][] = Object.keys(positivesByProperty).map(
    (key) => positivesByProperty[key],
  );

  return (dcr) => {
    for (const token of negatives) {
      if (!tokenMatches(token, dcr)) return false;
    }
    for (const group of positiveGroups) {
      if (!group.some((token: FilterToken) => tokenMatches(token, dcr))) return false;
    }
    return true;
  };
}
